import React, { Component } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CrossReferenceList from './components/Lists/CrossReferenceList';
import {
    BodyText, LargeGutter, SourceFootnotes, FootnoteReference,
} from './components/Core/TextComponents';
import { ThemeContext } from './components/Core/ThemeContext';
import { withAlert } from 'react-alert';
import SEO from './components/SEO';
import { buildCanonicalUrl, deriveLegacyCanonicalPath } from './utilities/canonicalUrls';
import { buildEntitySeoDescription, buildEntitySeoTitle } from './utilities/seo';
import osisConverters from './utilities/osisConversions';
import personPageOverrides from './data/personPageOverrides.json';

const querystring = require('querystring');

const getPersonPageOverride = (personRef = '') => (personRef ? personPageOverrides[personRef] || null : null);

const normalizePersonText = (value = '') => `${value}`
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim();

const extractLeadSentence = (value = '') => {
    const normalized = normalizePersonText(value);
    if (!normalized) return '';

    const match = normalized.match(/^.*?[.!?](?:\s|$)/);
    return match ? match[0].trim() : normalized;
};

const trimTrailingPunctuation = (value = '') => `${value}`.replace(/[.,;:!?]+$/g, '').trim();

const toSentence = (value = '') => {
    const normalized = normalizePersonText(value);
    if (!normalized) return '';
    return /[.!?]$/.test(normalized) ? normalized : `${normalized}.`;
};

const formatGender = (gender = '') => {
    if (gender.startsWith('F')) return 'female';
    if (gender.startsWith('M')) return 'male';
    if (gender.startsWith('P')) return 'group';
    return '';
};

const collectNamedRelations = (relations = []) => (
    Array.isArray(relations)
        ? relations.map((entry) => entry && entry.name).filter(Boolean)
        : []
);

const buildPeopleLead = (person = {}) => {
    const name = person.name || 'This person';
    const summaryLead = extractLeadSentence(person.dictText || '');
    const meaning = normalizePersonText(person.meaning || '');
    const gender = formatGender(person.gender || '');
    const father = collectNamedRelations(person.father);
    const mother = collectNamedRelations(person.mother);
    const partners = collectNamedRelations(person.partners);
    const children = collectNamedRelations(person.children);
    const referenceCount = Array.isArray(person.verses) ? person.verses.length : 0;

    const parts = [];
    if (summaryLead) {
        parts.push(summaryLead);
    }
    if (meaning) {
        parts.push(`${name} means "${meaning}."`);
    }
    if (gender) {
        parts.push(`${name} is identified here as ${gender}.`);
    }
    if (father.length > 0) {
        parts.push(`Father: ${father.join(', ')}.`);
    }
    if (mother.length > 0) {
        parts.push(`Mother: ${mother.join(', ')}.`);
    }
    if (partners.length > 0) {
        parts.push(`Partner${partners.length === 1 ? '' : 's'}: ${partners.join(', ')}.`);
    }
    if (children.length > 0) {
        parts.push(`Child${children.length === 1 ? '' : 'ren'}: ${children.join(', ')}.`);
    }
    if (person.birthYear) {
        parts.push(`Birth year: ${person.birthYear}.`);
    }
    if (person.deathYear) {
        parts.push(`Death year: ${person.deathYear}.`);
    }
    if (referenceCount > 0) {
        parts.push(`This profile links to ${referenceCount} Bible passage${referenceCount === 1 ? '' : 's'} on ai-BIBLE.`);
    }

    return parts.join(' ');
};

const buildPersonMeaningLabel = (person = {}) => {
    const meaning = trimTrailingPunctuation(person.meaning || '');
    return meaning ? `Meaning: ${meaning}` : '';
};

const buildPersonHeroMeta = (person = {}) => {
    const gender = formatGender(person.gender || '');
    const referenceCount = Array.isArray(person.verses) ? person.verses.length : 0;
    return [
        buildPersonMeaningLabel(person),
        gender ? gender.charAt(0).toUpperCase() + gender.slice(1) : '',
        referenceCount > 0 ? `${referenceCount} cross reference${referenceCount === 1 ? '' : 's'}` : '',
    ].filter(Boolean).join(' · ');
};

const buildPersonFallbackOverview = (person = {}) => {
    const name = person.name || 'This person';
    const meaning = trimTrailingPunctuation(person.meaning || '');
    const lead = extractLeadSentence(person.dictText || '');
    if (lead) return toSentence(lead);
    if (meaning) return `${name} means "${meaning}."`;
    return `${name} is a person mentioned in the Bible.`;
};

const buildPersonOverview = (person = {}) => {
    const override = getPersonPageOverride(person.ref);
    if (override?.overview) return override.overview;
    return buildPeopleLead(person) || buildPersonFallbackOverview(person);
};

const buildPersonSignificance = (person = {}) => {
    const override = getPersonPageOverride(person.ref);
    if (override?.biblicalSignificance) return override.biblicalSignificance;

    const lead = buildPersonFallbackOverview(person);
    const referenceCount = Array.isArray(person.verses) ? person.verses.length : 0;
    return `AI summary: ${lead} It appears in ${referenceCount} Bible passage${referenceCount === 1 ? '' : 's'}, and this page gathers those linked references to show how this figure fits into the biblical story.`;
};

const normalizeReferenceLabelToOsis = (value = '') => {
    const compact = `${value}`
        .replace(/[()[\]]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    if (!compact) return '';

    return compact
        .replace(/^([1-3])\s+([A-Za-z])/i, '$1$2')
        .replace(/\s*\.\s*/g, '.')
        .replace(/\s*:\s*/g, '.')
        .replace(/\s+/g, '');
};

const resolveSummaryReferenceOsis = (label = '', href = '') => {
    const hashRef = `${href}`.split('#')[1] || '';
    const normalizedHashRef = normalizeReferenceLabelToOsis(hashRef);
    if (normalizedHashRef) return normalizedHashRef;
    return normalizeReferenceLabelToOsis(label);
};

const buildAiBibleReferenceHref = (osisRef = '') => {
    const normalizedOsisRef = normalizeReferenceLabelToOsis(osisRef);
    if (!normalizedOsisRef) return '';
    return osisConverters.toWebReadPath(normalizedOsisRef);
};

const formatOsisReferenceLabel = (osisRef = '') => `${osisRef}`
    .replace(/^([1-3])([A-Za-z])/, '$1 $2')
    .replace(/\.(\d+)\.(\d+)$/, ' $1:$2')
    .replace(/\.(\d+)$/, ' $1');

const renderDictionarySummary = (value = '') => {
    const text = `${value}`;
    const parts = [];
    const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIndex = 0;
    let match;

    while ((match = linkPattern.exec(text)) !== null) {
        const [raw, label, href] = match;
        const matchIndex = match.index;

        if (matchIndex > lastIndex) {
            parts.push(text.slice(lastIndex, matchIndex));
        }

        const osisRef = resolveSummaryReferenceOsis(label, href);
        const aiBibleHref = buildAiBibleReferenceHref(osisRef);

        if (aiBibleHref) {
            parts.push(
                <a key={`${osisRef}-${matchIndex}`} href={aiBibleHref}>
                    {label}
                </a>,
            );
        } else {
            parts.push(raw);
        }

        lastIndex = matchIndex + raw.length;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts;
};

const buildAiGeneratedSource = () => ({
    footnote: 'AI-assisted summary prepared for ai-BIBLE and stored as page content, based on dictionary material, person metadata, and linked Bible cross references.',
});

const getSortedFootnoteEntries = (sources = []) => [...sources].sort((a, b) => {
    if (!a?.footnote) return -1;
    if (!b?.footnote) return 1;
    if (a.footnote > b.footnote) return 1;
    if (b.footnote > a.footnote) return -1;
    return 0;
});

const getFootnoteNumber = (sortedSources = [], predicate) => {
    let counter = 0;

    for (let i = 0; i < sortedSources.length; i += 1) {
        const source = sortedSources[i];
        if (!source?.footnote) continue;
        counter += 1;
        if (predicate(source)) return counter;
    }

    return null;
};

const sectionCardStyles = {
    pageShell: {
        width: 'min(1120px, calc(100% - 36px))',
        margin: '28px auto 70px',
    },
    stack: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    previewGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.2fr) minmax(280px, 0.8fr)',
        gap: 16,
        alignItems: 'stretch',
    },
    shell: {
        display: 'flex',
        background: '#fff',
        borderRadius: 15,
        overflow: 'hidden',
        boxShadow: '0 10px 24px rgba(86, 96, 110, 0.07)',
        border: '1px solid rgba(188, 198, 208, 0.38)',
    },
    strip: {
        width: 14,
        minWidth: 14,
        background: 'var(--color-grad-1)',
    },
    content: {
        flex: 1,
        background: 'linear-gradient(180deg, #fffefb 0%, #f7fbfb 100%)',
    },
    summaryButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '18px 22px',
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        textAlign: 'left',
    },
    summaryText: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
    },
    summaryEyebrow: {
        margin: 0,
        color: 'rgba(92, 108, 111, 0.82)',
        fontSize: '0.72em',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
    },
    summaryTitle: {
        margin: 0,
        color: 'rgba(0, 0, 0, 0.8)',
        fontSize: '1.05em',
        fontWeight: 700,
        letterSpacing: '0.01em',
    },
    summaryDivider: {
        height: 1,
        background: 'rgba(188, 198, 208, 0.32)',
        margin: '0 20px',
    },
    summaryBody: {
        padding: '18px 22px 22px',
        color: 'rgba(0, 0, 0, 0.76)',
    },
    collapsedBody: {
        maxHeight: 0,
        overflow: 'hidden',
        paddingTop: 0,
        paddingBottom: 0,
    },
    heroShell: {
        display: 'flex',
        background: 'linear-gradient(135deg, #fffdfa 0%, #f4faf9 100%)',
        borderRadius: 18,
        overflow: 'hidden',
        border: '1px solid rgba(188, 198, 208, 0.4)',
        boxShadow: '0 18px 42px rgba(86, 96, 110, 0.1)',
        marginBottom: 18,
    },
    heroStrip: {
        width: 22,
        minWidth: 22,
        background: 'var(--color-grad-1)',
    },
    heroBody: {
        padding: '30px 34px 28px',
        flex: 1,
    },
    heroEyebrow: {
        margin: 0,
        color: 'rgba(90, 105, 108, 0.84)',
        fontSize: '0.74em',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    heroTitle: {
        margin: '6px 0 0 0',
        fontSize: 'clamp(2.2em, 5vw, 3.1em)',
        color: 'rgba(0, 0, 0, 0.82)',
        fontFamily: 'Dancing Script, Georgia',
    },
    heroMeta: {
        margin: '10px 0 0 0',
        color: 'rgba(0, 0, 0, 0.58)',
        fontSize: '1.02em',
    },
    heroSummary: {
        marginTop: 14,
        marginBottom: 0,
        fontSize: '1.08em',
        lineHeight: 1.65,
    },
    heroFacts: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 10,
        marginTop: 20,
    },
    heroFact: {
        padding: '10px 12px',
        border: '1px solid rgba(188, 198, 208, 0.42)',
        borderRadius: 10,
        background: 'linear-gradient(145deg, #fff 0%, #f4faf9 100%)',
    },
    heroFactLabel: {
        margin: 0,
        color: 'rgba(92, 108, 111, 0.82)',
        fontSize: '0.68em',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
    },
    heroFactValue: {
        margin: '3px 0 0',
        color: 'rgba(23, 38, 83, 0.9)',
        fontSize: '0.88em',
    },
};

const relationshipStyles = {
    tree: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    row: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        alignItems: 'stretch',
    },
    rowCentered: {
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 10,
    },
    card: {
        display: 'block',
        flex: '1 1 160px',
        minWidth: 160,
        textDecoration: 'none',
        borderRadius: 10,
        border: '1px solid rgba(188, 198, 208, 0.45)',
        background: 'linear-gradient(180deg, #fffdfa 0%, #f8fbfb 100%)',
        boxShadow: '0 8px 18px rgba(86, 96, 110, 0.08)',
        padding: '8px 10px',
        color: 'inherit',
    },
    personCard: {
        display: 'block',
        width: 'min(300px, 100%)',
        textDecoration: 'none',
        borderRadius: 12,
        border: '1px solid rgba(188, 198, 208, 0.45)',
        background: 'linear-gradient(180deg, #fffefb 0%, #f5faf8 100%)',
        boxShadow: '0 10px 20px rgba(86, 96, 110, 0.09)',
        padding: '10px 12px',
        color: 'inherit',
    },
    cardLabel: {
        margin: 0,
        color: 'rgba(92, 108, 111, 0.82)',
        fontSize: '0.72em',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
    },
    cardTitle: {
        margin: '4px 0 0 0',
        fontSize: '1.06em',
        fontWeight: 700,
        color: 'rgba(0, 0, 0, 0.82)',
    },
    cardMeta: {
        margin: '3px 0 0 0',
        color: 'rgba(0, 0, 0, 0.62)',
        fontSize: '0.9em',
    },
    connector: {
        width: 2,
        height: 8,
        margin: '0 auto',
        background: 'linear-gradient(180deg, rgba(225,193,77,0.85) 0%, rgba(232,214,142,0.25) 100%)',
        borderRadius: 999,
    },
    lowerGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 8,
        alignItems: 'start',
    },
    collection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '8px 10px',
        borderRadius: 10,
        border: '1px solid rgba(188, 198, 208, 0.35)',
        background: 'rgba(255, 255, 255, 0.72)',
    },
    collectionTitle: {
        margin: 0,
        color: 'rgba(92, 108, 111, 0.82)',
        fontSize: '0.72em',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
    },
    pills: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
    },
    pill: {
        display: 'inline-flex',
        flexDirection: 'column',
        gap: 1,
        padding: '6px 9px',
        borderRadius: 999,
        border: '1px solid rgba(188, 198, 208, 0.45)',
        background: '#fff',
        textDecoration: 'none',
        color: 'inherit',
        boxShadow: '0 6px 14px rgba(86, 96, 110, 0.06)',
    },
};

function SectionCard({
    title,
    children,
    startsOpen = true,
    eyebrow = 'Section',
    open: controlledOpen,
    onToggle,
}) {
    const [internalOpen, setInternalOpen] = React.useState(startsOpen);
    const isControlled = typeof controlledOpen === 'boolean';
    const open = isControlled ? controlledOpen : internalOpen;

    const setOpen = (nextValue) => {
        if (!isControlled) {
            setInternalOpen(nextValue);
        }

        if (onToggle) {
            onToggle(nextValue);
        }
    };

    return (
        <section style={sectionCardStyles.shell}>
            <div style={sectionCardStyles.strip} aria-hidden="true" />
            <div style={sectionCardStyles.content}>
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    aria-expanded={open}
                    style={sectionCardStyles.summaryButton}
                >
                    <span style={sectionCardStyles.summaryText}>
                        <span style={sectionCardStyles.summaryEyebrow}>{eyebrow}</span>
                        <h2 style={sectionCardStyles.summaryTitle}>{title}</h2>
                    </span>
                    <ExpandMoreIcon
                        style={{
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 140ms ease',
                            color: 'rgba(83, 102, 106, 0.85)',
                        }}
                    />
                </button>
                <div style={sectionCardStyles.summaryDivider} aria-hidden="true" />
                <div
                    aria-hidden={!open}
                    style={{
                        ...sectionCardStyles.summaryBody,
                        ...(open ? null : sectionCardStyles.collapsedBody),
                    }}
                >
                    {children}
                </div>
            </div>
        </section>
    );
}

function HeroCard({ title, meta, summary, facts = [] }) {
    return (
        <div style={sectionCardStyles.heroShell}>
            <div style={sectionCardStyles.heroStrip} />
            <div style={sectionCardStyles.heroBody}>
                <p style={sectionCardStyles.heroEyebrow}>Bible Person</p>
                <h1 style={sectionCardStyles.heroTitle}>{title}</h1>
                {meta ? <p style={sectionCardStyles.heroMeta}>{meta}</p> : null}
                {summary ? (
                    <BodyText style={sectionCardStyles.heroSummary}>
                        {summary}
                    </BodyText>
                ) : null}
                {facts.length > 0 ? (
                    <div style={sectionCardStyles.heroFacts}>
                        {facts.map((fact) => (
                            <div key={fact.label} style={sectionCardStyles.heroFact}>
                                <p style={sectionCardStyles.heroFactLabel}>{fact.label}</p>
                                <p style={sectionCardStyles.heroFactValue}>{fact.value}</p>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function RelationshipCard({
    label,
    name,
    meaning,
    refId,
}) {
    const content = (
        <>
            <p style={relationshipStyles.cardLabel}>{label}</p>
            <p style={relationshipStyles.cardTitle}>{name || 'Unknown'}</p>
            {meaning ? <p style={relationshipStyles.cardMeta}>Meaning: {meaning}</p> : null}
        </>
    );

    if (refId) {
        return (
            <a href={`/person/${refId}`} style={relationshipStyles.card}>
                {content}
            </a>
        );
    }

    return (
        <div style={relationshipStyles.card}>
            {content}
        </div>
    );
}

function RelationshipPill({ person = {} }) {
    const meaning = trimTrailingPunctuation(person.meaning || '');
    const content = (
        <>
            <span style={{ fontWeight: 700, color: 'rgba(0, 0, 0, 0.82)' }}>{person.name || 'Unknown'}</span>
            {meaning ? (
                <span style={{ color: 'rgba(0, 0, 0, 0.62)', fontSize: '0.92em' }}>
                    Meaning: {meaning}
                </span>
            ) : null}
        </>
    );

    if (person.ref) {
        return (
            <a href={`/person/${person.ref}`} style={relationshipStyles.pill}>
                {content}
            </a>
        );
    }

    return (
        <div style={relationshipStyles.pill}>
            {content}
        </div>
    );
}

function RelationshipCollection({ title, people = [] }) {
    if (!Array.isArray(people) || people.length === 0) return null;

    return (
        <div style={relationshipStyles.collection}>
            <p style={relationshipStyles.collectionTitle}>{title}</p>
            <div style={relationshipStyles.pills}>
                {people.map((entry, index) => (
                    <RelationshipPill key={`${entry.ref || entry.name || 'person'}-${index}`} person={entry} />
                ))}
            </div>
        </div>
    );
}

function FamilyTreeSection({ person = {} }) {
    const father = Array.isArray(person.father) ? person.father[0] : null;
    const mother = Array.isArray(person.mother) ? person.mother[0] : null;
    const hasParents = father || mother;
    const lowerCollections = [
        { title: 'Siblings', people: person.siblings },
        { title: 'Partners', people: person.partners },
        { title: 'Children', people: person.children },
    ].filter((entry) => Array.isArray(entry.people) && entry.people.length > 0);
    const hasLowerRelations = lowerCollections.length > 0;
    const parentCount = [father, mother].filter(Boolean).length;

    return (
        <div style={relationshipStyles.tree}>
            {hasParents ? (
                <div
                    style={{
                        ...relationshipStyles.row,
                        justifyContent: parentCount === 1 ? 'center' : 'stretch',
                    }}
                >
                    {father ? (
                        <RelationshipCard
                            label="Father"
                            name={father.name}
                            meaning={trimTrailingPunctuation(father.meaning || '')}
                            refId={father.ref}
                        />
                    ) : null}
                    {mother ? (
                        <RelationshipCard
                            label="Mother"
                            name={mother.name}
                            meaning={trimTrailingPunctuation(mother.meaning || '')}
                            refId={mother.ref}
                        />
                    ) : null}
                </div>
            ) : null}
            {hasParents ? <div style={relationshipStyles.connector} aria-hidden="true" /> : null}
            <div style={relationshipStyles.rowCentered}>
                <div style={relationshipStyles.personCard}>
                    <p style={relationshipStyles.cardLabel}>Central Figure</p>
                    <p style={relationshipStyles.cardTitle}>{person.name || 'Unknown'}</p>
                    {person.meaning ? (
                        <p style={relationshipStyles.cardMeta}>Meaning: {trimTrailingPunctuation(person.meaning)}</p>
                    ) : null}
                    {person.dictText ? (
                        <p style={{ ...relationshipStyles.cardMeta, marginTop: 5 }}>
                            {extractLeadSentence(person.dictText)}
                        </p>
                    ) : null}
                </div>
            </div>
            {hasLowerRelations ? <div style={relationshipStyles.connector} aria-hidden="true" /> : null}
            {hasLowerRelations ? (
                <div style={relationshipStyles.lowerGrid}>
                    {lowerCollections.map((entry) => (
                        <RelationshipCollection key={entry.title} title={entry.title} people={entry.people} />
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function RelatedGeographySection({ relatedGeography = [] }) {
    if (!Array.isArray(relatedGeography) || relatedGeography.length === 0) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {relatedGeography.map((entry, index) => (
                <a
                    key={`${entry.ref || entry.name || 'geo'}-${index}`}
                    href={entry.ref ? `/location/${entry.ref}` : undefined}
                    style={{
                        display: 'block',
                        textDecoration: 'none',
                        borderRadius: 10,
                        border: '1px solid rgba(188, 198, 208, 0.45)',
                        background: 'linear-gradient(180deg, #fffdfa 0%, #f8fbfb 100%)',
                        boxShadow: '0 8px 18px rgba(86, 96, 110, 0.08)',
                        padding: '12px 14px',
                        color: 'inherit',
                    }}
                >
                    <p style={relationshipStyles.cardLabel}>Related Place</p>
                    <p style={relationshipStyles.cardTitle}>{entry.name}</p>
                    {entry.reason ? (
                        <p style={{ ...relationshipStyles.cardMeta, marginTop: 8 }}>{entry.reason}</p>
                    ) : null}
                </a>
            ))}
        </div>
    );
}

function EntityDetailGrid({ person = {} }) {
    const details = [
        ['Name meaning', person.meaning],
        ['Identity', formatGender(person.gender)],
        ['Birth place', person.birthPlace?.name],
        ['Death place', person.deathPlace?.name],
        ['Birth year', person.birthYear],
        ['Death year', person.deathYear],
    ].filter(([, value]) => value);

    if (!details.length) return null;

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            {details.map(([label, value]) => (
                <div key={label} style={{ padding: '10px 12px', borderRadius: 9, background: '#f5faf9', border: '1px solid rgba(188, 198, 208, 0.38)' }}>
                    <p style={{ margin: 0, fontSize: '0.68em', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(92, 108, 111, 0.82)' }}>{label}</p>
                    <p style={{ margin: '3px 0 0', color: 'rgba(23, 38, 83, 0.9)' }}>{value}</p>
                </div>
            ))}
        </div>
    );
}

class PeopleResult extends Component {
    static contextType = ThemeContext;

    constructor() {
        super();

        this.state = {
            data: {
                who: [],
            },
            sources: [],
            ref: '',
            sourcesOpen: false,
        };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    getPerson() {
        if (window.location.pathname.includes('/people')) {
            return { ...querystring.parse(window.location.search.substr(1)), pathPrefix: '/data/' };
        }
        return { rec: window.location.pathname.replace('/person/', ''), pathPrefix: '/data/' };
    }

    componentDidMount() {
        const values = this.getPerson();
        const { token } = localStorage;
        this.context.loading(true);

        if (window.web_version && values.rec) {
            const normalizedPath = deriveLegacyCanonicalPath(window.location.pathname, window.location.search);
            if (`${window.location.pathname}${window.location.search}` !== normalizedPath) {
                window.history.replaceState({}, '', normalizedPath);
            }
        }

        this.setState({ ref: values.rec });

        let endpoint = `/v1/people/known?rec=${values.rec}`;

        if (process.env.REACT_APP_AWS_API) {
            endpoint = window.API_BASE + endpoint;
        }

        endpoint = `${values.pathPrefix}people/${values.rec}.json`;

        if (endpoint !== '') {
            fetch(endpoint, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
                .then((res) => res.json())
                .then((response) => {
                    const data = { who: response.who.person };
                    const { sources } = response;

                    this.setState({
                        data,
                        sources,
                    }, () => {
                        this.context.loading(false);
                        this.openSourcesForFootnoteHash(window.location.hash);
                    });
                }).catch(() => {
                    this.context.loading(false);
                    this.props.alert.show('could not connect to server');
                });
        }
    }

    componentDidUpdate() {
        const values = this.getPerson();

        if (this.state.ref !== values.rec) {
            this.setState({ ref: values.rec });
            this.componentDidMount();
            return;
        }

        this.openSourcesForFootnoteHash(window.location.hash);
    }

    openSourcesForFootnoteHash = (hash = '') => {
        if (!/^#footnote\d+$/.test(hash)) return;
        if (this.state.sourcesOpen) return;

        this.setState({ sourcesOpen: true }, () => {
            window.requestAnimationFrame(() => {
                const target = document.querySelector(hash);
                if (target) {
                    target.scrollIntoView({ block: 'start' });
                }
            });
        });
    }

    render() {
        const person = this.state.data.who || {};
        const why = {
            cross_references: (person.verses || []),
        };
        const override = getPersonPageOverride(person.ref);
        const overview = buildPersonOverview(person);
        const significance = buildPersonSignificance(person);
        const heroMeta = buildPersonHeroMeta(person);
        const aiGeneratedSource = buildAiGeneratedSource();
        const allSources = [...this.state.sources, aiGeneratedSource];
        const sortedFootnotes = getSortedFootnoteEntries(allSources);
        const aiGeneratedFootnoteNumber = getFootnoteNumber(sortedFootnotes, (source) => source === aiGeneratedSource);
        const dictionarySummaryFootnoteNumber = getFootnoteNumber(
            sortedFootnotes,
            (source) => source?.footnote === this.state.sources?.[2]?.footnote,
        );

        const seoTitle = person.name ? buildEntitySeoTitle({
            type: 'person',
            name: person.name,
            summary: overview || person.dictText || person.meaning || '',
            crossReferenceCount: (person.verses || []).length,
        }) : 'Bible People | ai-BIBLE';
        document.title = seoTitle;

        const searchedRec = this.getPerson().rec;
        const canonical = buildCanonicalUrl(`/person/${searchedRec}`);
        const description = buildEntitySeoDescription({
            type: 'person',
            name: person.name || 'This person',
            summary: overview || person.dictText || person.meaning || '',
            crossReferenceCount: (person.verses || []).length,
        });
        const firstCrossReference = Array.isArray(person.verses) && person.verses.length > 0
            ? person.verses[0]
            : '';
        const firstCrossReferenceHref = buildAiBibleReferenceHref(firstCrossReference);
        const heroFacts = [
            person.meaning ? { label: 'Name meaning', value: person.meaning } : null,
            person.gender ? { label: 'Identity', value: formatGender(person.gender) } : null,
            firstCrossReference ? { label: 'First appears', value: formatOsisReferenceLabel(firstCrossReference) } : null,
        ].filter(Boolean);
        const structuredData = person.name ? {
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: person.name,
            description: overview || undefined,
            url: canonical,
            gender: formatGender(person.gender || '') || undefined,
            birthDate: person.birthYear ? `${person.birthYear}` : undefined,
            deathDate: person.deathYear ? `${person.deathYear}` : undefined,
            sameAs: (person.verses || []).slice(0, 10).map(buildAiBibleReferenceHref).filter(Boolean),
            mainEntityOfPage: canonical,
        } : null;

        return (
            <div className="entity-page-viewport">
                <SEO
                    canonical={canonical}
                    title={seoTitle}
                    description={description}
                    type="article"
                    structuredData={structuredData}
                />
                <div style={sectionCardStyles.pageShell}>
                    <HeroCard
                        title={person.name}
                        meta={heroMeta}
                        summary={overview ? (
                            <>
                                {overview}
                                {firstCrossReferenceHref ? (
                                    <>
                                        {' '}
                                        <a href={firstCrossReferenceHref}>
                                            First appears in {formatOsisReferenceLabel(firstCrossReference)}
                                        </a>.
                                    </>
                                ) : null}
                                {aiGeneratedFootnoteNumber ? <FootnoteReference number={aiGeneratedFootnoteNumber} /> : null}
                            </>
                        ) : null}
                        facts={heroFacts}
                    />
                    <div style={sectionCardStyles.stack}>
                        <div className="entity-preview-grid" style={sectionCardStyles.previewGrid}>
                            {significance ? (
                                <SectionCard title="Why this person matters" eyebrow="Interpretation">
                                    <BodyText>
                                        {significance}
                                        {aiGeneratedFootnoteNumber ? <FootnoteReference number={aiGeneratedFootnoteNumber} /> : null}
                                    </BodyText>
                                </SectionCard>
                            ) : null}
                            <SectionCard title="Family Tree" eyebrow="Relationships">
                                <FamilyTreeSection person={person} />
                            </SectionCard>
                        </div>
                        <SectionCard title="Profile Details" eyebrow="At a glance">
                            <EntityDetailGrid person={person} />
                        </SectionCard>
                        {(override?.relatedGeography && override.relatedGeography.length > 0) ? (
                            <SectionCard title="Related Geography" eyebrow="Places">
                                <BodyText>
                                    This section highlights places directly relevant to this person's role in the biblical story.
                                    <br />
                                </BodyText>
                                <LargeGutter />
                                <RelatedGeographySection relatedGeography={override.relatedGeography} />
                            </SectionCard>
                        ) : null}
                        {person.dictText ? (
                            <SectionCard title="Easton's Dictionary · Historical and Archaic" startsOpen={false} eyebrow="Reference">
                                <BodyText>
                                    {renderDictionarySummary(person.dictText)}
                                    {' '}
                                    {dictionarySummaryFootnoteNumber ? (
                                        <FootnoteReference number={dictionarySummaryFootnoteNumber} />
                                        ) : null}
                                </BodyText>
                                <BodyText style={{ marginTop: 12, fontSize: '0.88em', color: 'rgba(92, 80, 52, 0.9)' }}>
                                    Source note: this 1893 dictionary language reflects its period and may be culturally insensitive or outdated. It is presented as historical source material, not as ai-BIBLE’s language, interpretation, or endorsement.
                                </BodyText>
                            </SectionCard>
                        ) : null}
                        <SectionCard title="Cross References" startsOpen={false} eyebrow="Texts">
                            <CrossReferenceList
                                why={why}
                                sources={(this.state.sources.length > 0) ? [this.state.sources[1]] : null}
                                figureNumber={1}
                            />
                        </SectionCard>
                        <SectionCard
                            title="Sources"
                            startsOpen={false}
                            eyebrow="Footnotes"
                            open={this.state.sourcesOpen}
                            onToggle={(sourcesOpen) => this.setState({ sourcesOpen })}
                        >
                            <SourceFootnotes footnotes={[...allSources]} footnoteStart={1} />
                        </SectionCard>
                    </div>
                </div>
            </div>
        );
    }
}

export default withAlert()(PeopleResult);
